import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { useUserQuery, useUpdateUserMutation } from '@/hooks/queries/useUsers';
import { usePropertiesDropdownQuery } from '@/hooks/queries/useDropdowns';
import { UserManagementGuard } from '@/components/guards/role-guard';
import { cn } from '@/lib/utils';

// Components
import BasicInfoSection from './components/basic-info-section';
import RolePermissionsSection from './components/role-permissions-section';
import PasswordResetSection from './components/password-reset-section';

// Types
import { formSchema, EditUserFormData } from '../../../../schema/user';

export default function EditUserPage() {
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // API queries
  const {
    data: user,
    isLoading: userLoading,
    error: userError,
  } = useUserQuery(id!);
  const { data: properties = [] } = usePropertiesDropdownQuery();
  const updateUserMutation = useUpdateUserMutation(id!, {
    onSuccessCallback: () => {
      navigate('/users');
    },
  });

  const form = useForm<EditUserFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      role: 'auditor',
      status: 'Active',
      property_id: undefined,
      phone: '',
      department: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    if (user) {
      form.reset({
        name: user.name || '',
        email: user.email || '',
        role:
          (user.roles?.[0]?.toLowerCase() as
            | 'admin'
            | 'super_admin'
            | 'manager'
            | 'auditor') || 'auditor',
        status: (user as any).is_active !== false ? 'Active' : 'Inactive',
        property_id: user.property_id || undefined,
        phone: user.phone || '',
        department: user.department || '',
        newPassword: '',
        confirmPassword: '',
      });
    }
  }, [user, form]);

  // Loading state
  if (userLoading) {
    return (
      <UserManagementGuard>
        <div
          className={cn(
            'flex flex-col gap-4 sm:gap-8 p-3 sm:p-6',
            isRTL && 'rtl'
          )}
        >
          <Card>
            <CardContent className="p-6">
              <div className={cn('text-center', isRTL && 'text-right')}>
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {t('users.edit.loading', 'Loading user details...')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </UserManagementGuard>
    );
  }

  // User not found
  if (userError || !user) {
    return (
      <UserManagementGuard>
        <div
          className={cn(
            'flex flex-col gap-4 sm:gap-8 p-3 sm:p-6',
            isRTL && 'rtl'
          )}
        >
          <Card>
            <CardContent className="p-6">
              <div className={cn('text-center', isRTL && 'text-right')}>
                <p className="text-muted-foreground">
                  {t('users.edit.userNotFound')}
                </p>
                <Button onClick={() => navigate('/users')} className="mt-4">
                  <ArrowLeft
                    className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')}
                  />
                  {t('users.edit.backToUsers')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </UserManagementGuard>
    );
  }

  function onSubmit(values: EditUserFormData) {
    if (!user) return;

    const updateData = {
      name: values.name,
      email: values.email,
      role: values.role,
      is_active: values.status === 'Active',
      property_id: values.property_id ?? undefined,
      ...(values.newPassword && { password: values.newPassword }),
    };

    updateUserMutation.mutate(updateData);
  }

  return (
    <UserManagementGuard>
      <div
        className={cn(
          'flex flex-col gap-4 sm:gap-8 p-3 sm:p-6',
          isRTL && 'rtl'
        )}
      >
        {/* Header */}
        <div
          className={cn(
            'flex flex-col sm:flex-row sm:items-center justify-between gap-3'
          )}
        >
          <div className={cn('flex items-center gap-3')}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/users')}
              className="shrink-0"
            >
              <ArrowLeft className={cn('h-4 w-4 mx-2')} />
              {t('users.edit.buttons.back')}
            </Button>
            <div className={cn('space-y-1', isRTL && 'text-right')}>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold font-headline text-primary">
                {t('users.edit.title')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t('users.edit.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl">
          <Card className="bg-card/50 backdrop-blur-sm border-border/50">
            <CardHeader className="p-3 sm:p-6">
              <CardTitle
                className={cn(
                  'font-headline text-primary text-base sm:text-lg',
                  isRTL && 'text-right'
                )}
              >
                {t('users.edit.userInformation')}
              </CardTitle>
              <CardDescription className={cn('text-sm', isRTL && 'text-right')}>
                {t('users.edit.userInformationDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  {/* Basic Information */}
                  <BasicInfoSection control={form.control} />

                  <Separator />

                  {/* Role & Permissions */}
                  <RolePermissionsSection
                    control={form.control}
                    properties={properties}
                  />

                  <Separator />

                  {/* Password Reset */}
                  <PasswordResetSection control={form.control} />

                  {/* Actions */}
                  <div
                    className={cn(
                      'flex flex-col sm:flex-row justify-end gap-2 pt-4',
                      isRTL && 'sm:flex-row-reverse'
                    )}
                  >
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate('/users')}
                      disabled={updateUserMutation.isPending}
                      className="w-full sm:w-auto"
                    >
                      {t('users.edit.buttons.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      disabled={updateUserMutation.isPending}
                      className="w-full sm:w-auto"
                    >
                      {updateUserMutation.isPending && (
                        <Loader2
                          className={cn(
                            'h-4 w-4 animate-spin',
                            isRTL ? 'ml-2' : 'mr-2'
                          )}
                        />
                      )}
                      <Save
                        className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')}
                      />
                      {updateUserMutation.isPending
                        ? t('users.edit.buttons.updating')
                        : t('users.edit.buttons.updateUser')}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </UserManagementGuard>
  );
}
